import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { AField, ABtn } from "./ui";

const EMPTY = { title: "", description: "", duration: "", is_preview: false, video_type: "none", external_url: "" };

const VideoUpload = ({ lessonId, onDone }) => {
  const [pct, setPct] = useState(null);
  const upload = async (f) => {
    if (!f) return;
    if (!["video/mp4", "video/webm", "video/quicktime"].includes(f.type)) return toast.error("الصيغ المدعومة: MP4, WEBM, MOV");
    const fd = new FormData();
    fd.append("file", f);
    setPct(0);
    try {
      const { data } = await api.post(`/admin/lessons/${lessonId}/video`, fd, { onUploadProgress: (e) => setPct(Math.round((100 * e.loaded) / (e.total || 1))) });
      toast.success("تم رفع الفيديو");
      onDone(data);
    } catch (e) { toast.error(errMsg(e, "فشل رفع الفيديو")); } finally { setPct(null); }
  };
  return (
    <label className="flex items-center gap-3 cursor-pointer rounded-xl border border-dashed border-white/15 hover:border-[var(--kh-violet)] p-4">
      {pct === null ? <Upload size={18} className="text-slate-400" /> : <Loader2 size={18} className="animate-spin" />}
      <span className="text-sm text-slate-300">{pct === null ? "اختار ملف فيديو (MP4 مفضل، حتى 1 جيجا)" : pct < 100 ? `جاري الرفع ${pct}%` : "جاري المعالجة والحفظ في التخزين المحمي..."}</span>
      <input type="file" hidden accept="video/mp4,video/webm,video/quicktime" disabled={pct !== null} onChange={(e) => upload(e.target.files[0])} data-testid="lesson-video-file-input" />
    </label>
  );
};

export const LessonDialog = ({ open, onClose, courseId, moduleId, lesson, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [cur, setCur] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lastOpen, setLastOpen] = useState(false);
  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) { setF(lesson ? { ...EMPTY, ...lesson } : EMPTY); setCur(lesson || null); }
  }
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    if (!f.title.trim()) return toast.error("اكتب عنوان الدرس");
    setBusy(true);
    const body = { title: f.title, description: f.description, duration: f.duration, is_preview: f.is_preview,
      video_type: f.video_type, external_url: f.external_url, module_id: moduleId };
    try {
      const { data } = cur ? await api.put(`/admin/lessons/${cur.id}`, body) : await api.post(`/admin/courses/${courseId}/lessons`, body);
      setCur(data);
      toast.success("تم حفظ الدرس");
      onSaved();
      if (f.video_type !== "upload" || data.video_path) onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl bg-[var(--kh-ws-card)] border-white/10 text-slate-100 max-h-[90vh] overflow-y-auto" data-testid="lesson-dialog">
        <DialogHeader><DialogTitle className="text-right font-heading">{cur ? "تعديل الدرس" : "درس جديد"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <AField label="عنوان الدرس" value={f.title} onChange={set("title")} testid="lesson-title-input" />
          <AField label="الوصف" textarea value={f.description} onChange={set("description")} testid="lesson-description-input" />
          <AField label="المدة (مثال 12:30)" value={f.duration} onChange={set("duration")} testid="lesson-duration-input" />
          <label className="flex items-center justify-between rounded-xl bg-white/5 px-4 py-3">
            <span className="text-sm">درس معاينة مجانية</span>
            <Switch checked={f.is_preview} onCheckedChange={(v) => setF({ ...f, is_preview: v })} data-testid="lesson-preview-switch" />
          </label>
          <div className="space-y-2">
            <span className="text-xs text-slate-400">مصدر الفيديو</span>
            <div className="grid grid-cols-3 gap-2">
              {[["none", "بدون"], ["upload", "رفع مباشر (محمي)"], ["external", "رابط خارجي"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setF({ ...f, video_type: v })} data-testid={`lesson-video-type-${v}`}
                  className={`px-3 py-2 rounded-xl text-xs border ${f.video_type === v ? "border-[var(--kh-violet)] bg-[var(--kh-violet)]/15" : "border-white/10"}`}>{l}</button>
              ))}
            </div>
          </div>
          {f.video_type === "external" && (
            <AField label="رابط الفيديو" dir="ltr" placeholder="https://www.youtube.com/watch?v=..." value={f.external_url} onChange={set("external_url")} testid="lesson-external-url-input"
              hint="يدعم YouTube (يفضل Unlisted) و Vimeo، أو رابط مباشر لملف MP4. الروابط الخارجية أقل حماية من الرفع المباشر." />
          )}
          {f.video_type === "upload" && (cur ? (
            <div className="space-y-2">
              {cur.video_path && <p className="text-xs text-emerald-300" data-testid="lesson-video-uploaded">فيديو مرفوع: {cur.video_filename}</p>}
              <VideoUpload lessonId={cur.id} onDone={(d) => { setCur(d); onSaved(); }} />
            </div>
          ) : <p className="text-xs text-yellow-300">احفظ الدرس الأول، وبعدها ارفع الفيديو.</p>)}
          <div className="flex justify-end gap-2 pt-2">
            <ABtn variant="ghost" onClick={onClose}>إغلاق</ABtn>
            <ABtn onClick={save} disabled={busy} data-testid="lesson-save-btn">{busy && <Loader2 size={15} className="animate-spin" />}حفظ</ABtn>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
